/*
 * What the brand websites send the Steward (draft 6b): a request for a price, the instant quoter, "Text me this quote",
 * newsletter and popup signups, and job applications. Every one carries where the visitor came from (first and last
 * touch) and two bot checks: a hidden field people never fill in, and how long the form was open.
 *
 * The price list never leaves the server. The website sends answers and gets a price back.
 */
import { z } from "zod";
import { lawnAnswersShape } from "./lawn-prices.js";
import { hourlyAnswersShape, PRICE_OFTEN } from "./prices.js";
import { APPLY_LANGS, NO_FIT_REASONS, screenScores, trialScores } from "./hiring.js";
import { HEARD_FROM } from "./heard-from.js";

const text = (max: number) => z.string().trim().max(max);
const optional = (max: number) => text(max).optional();

/** Where a visit came from, as the website saw it. Every value is optional and short. */
export const touchShape = z
  .object({
    utm_source: optional(200),
    utm_medium: optional(200),
    utm_campaign: optional(200),
    utm_term: optional(200),
    utm_content: optional(200),
    gclid: optional(300),
    fbclid: optional(300),
    msclkid: optional(300),
    landing: optional(500),
    referrer: optional(500),
    at: optional(40),
  })
  .strict();
export type Touch = z.infer<typeof touchShape>;

const siteGuard = {
  /** Where the visitor came from: the first time they came, and this time. */
  touch: z.object({ first: touchShape.optional(), last: touchShape.optional() }).strict().optional(),
  /** The hidden field. People never see it; bots fill it in. */
  website: z.string().max(200).optional(),
  /** How long the form was open, in milliseconds. Under 3 seconds is a bot. */
  openedMs: z.number().int().min(0).max(86_400_000),
};

const zip = z
  .string()
  .trim()
  .regex(/^\d{5}$/, "Type a 5 digit ZIP code.");
const phone = z
  .string()
  .trim()
  .regex(/^[+()\d\s.-]{10,20}$/, "Type a phone number with area code.");

/** The answers the instant quoter prices from. At most 24 add-on answers per call. */
export const homeAnswersShape = z
  .object({
    serviceId: text(20).min(1),
    sizeBy: z.enum(["rooms", "sqft"]).default("rooms"),
    beds: z.number().int().min(0).max(20).default(0),
    baths: z.number().min(0).max(20).default(0),
    other: z.number().int().min(0).max(20).default(0),
    sqft: z.number().int().min(0).max(50_000).default(0),
    often: z.enum(PRICE_OFTEN).default("once"),
    extras: z
      .record(z.string().max(20), z.union([z.boolean(), z.number().int().min(0).max(50)]))
      .refine((r) => Object.keys(r).length <= 24, "Too many answers.")
      .default({}),
    /** The office kept the price after the clean showed a bigger home (Let it pass, owner 2026-11-02). */
    letPass: z.boolean().optional(),
  })
  .strict();

/**
 * What a website quoter sends: a lawn's answers (kind lawn, the GroundsMen's prices), maid service by the hour (kind
 * hourly) or the price list's.
 */
export const siteAnswersShape = z.union([lawnAnswersShape, hourlyAnswersShape, homeAnswersShape]);
export type SiteAnswers = z.infer<typeof siteAnswersShape>;

export const TIMES_OF_DAY = ["morning", "afternoon", "flexible"] as const;

/** The words on the box for offers by text on every brand's quote form, the old Steward's own (owner answer 123). */
export const TEXT_OFFERS_WORDS = "Also text me occasional offers.";

export const siteRequest = z
  .object({
    name: text(120).min(1, "Type your name."),
    phone,
    email: z.email().max(200).optional(),
    zip,
    address: optional(300),
    service: optional(80),
    note: optional(2000),
    /** A day they would like, never today (no same-day booking from the website). */
    wantedDay: z
      .string()
      .regex(/^\d{4}-\d{2}-\d{2}$/)
      .optional(),
    timeOfDay: z.enum(TIMES_OF_DAY).default("flexible"),
    /** When the visitor priced it on the quote form first. */
    answers: siteAnswersShape.optional(),
    /**
     * The box for offers by text, ticked, with the exact words the page showed (proof of consent, A2P 10DLC). Each brand's
     * form uses TEXT_OFFERS_WORDS (owner answer 123); a form that sends no words is taken to have shown those.
     */
    textOffers: z
      .object({ ok: z.literal(true), words: text(300).min(10).default(TEXT_OFFERS_WORDS) })
      .strict()
      .optional(),
    /** The referral code from a client's link (?ref=CODE). */
    ref: z
      .string()
      .trim()
      .regex(/^[A-Z0-9]{4,16}$/)
      .optional(),
    /** "How did you hear about us?" on the form. */
    heardFrom: z.enum(HEARD_FROM).optional(),
    /** The form's own id for this request. A form not sure the first try arrived sends it again; it is filed once. */
    leadId: z
      .string()
      .trim()
      .regex(/^[A-Za-z0-9_-]{6,60}$/)
      .optional(),
    ...siteGuard,
  })
  .strict();
export type SiteRequest = z.infer<typeof siteRequest>;

export const siteQuoteAsk = z.object({ zip, answers: siteAnswersShape }).strict();
export type SiteQuoteAsk = z.infer<typeof siteQuoteAsk>;

export const siteQuoteAnswer = z.object({
  /** null when the list has no price for these answers (the website shows the request form instead). */
  price: z
    .object({
      service: z.string(),
      firstVisitCents: z.number(),
      repeat: z.object({ label: z.string(), perVisitCents: z.number() }).nullable(),
      extras: z.array(z.object({ label: z.string(), cents: z.number() })),
    })
    .nullable(),
});
export type SiteQuoteAnswer = z.infer<typeof siteQuoteAnswer>;

export const siteTextMe = z
  .object({
    name: text(120).min(1, "Type your name."),
    phone,
    email: z.email().max(200).optional(),
    zip,
    answers: siteAnswersShape,
    ...siteGuard,
  })
  .strict();
export type SiteTextMe = z.infer<typeof siteTextMe>;

export const siteSignup = z
  .object({
    kind: z.enum(["newsletter", "popup"]),
    name: optional(120),
    email: z.email().max(200).optional(),
    phone: phone.optional(),
    zip: zip.optional(),
    offer: optional(120),
    ...siteGuard,
  })
  .strict()
  .refine((s) => Boolean(s.email ?? s.phone), { message: "Type an email or a phone number.", path: ["email"] });
export type SiteSignup = z.infer<typeof siteSignup>;

/** The answers both careers forms may send (owner, 2026-11-04). */
const applyAnswers = {
  language: z.enum(APPLY_LANGS).optional(),
  role: optional(30),
  /** Days they can work, 1 Monday to 7 Sunday. */
  days: z.array(z.number().int().min(1).max(7)).max(7).optional(),
  ride: z.boolean().optional(),
  workOk: z.boolean().optional(),
  start: optional(40),
  /** They ticked the box to get texts about their application. */
  textOk: z.boolean().optional(),
  /** Where they found us: indeed, flyer, truck, facebook... from ?src= on the link. */
  src: optional(40),
  /** The crew member who sent them, from ?ref= on their refer link. */
  ref: optional(40),
};

export const siteApply = z
  .object({
    name: text(120).min(1, "Type your name."),
    phone,
    email: z.email().max(200).optional(),
    zip: zip.optional(),
    position: optional(80),
    about: optional(3000),
    ...applyAnswers,
    ...siteGuard,
  })
  .strict();
export type SiteApply = z.infer<typeof siteApply>;

/**
 * The Steward's own careers page (/apply/<brand>, owner 2026-11-04): short and phone first, English or Spanish. Answers
 * that do not fit (no ride, can't work weekdays) only flag the card; nobody is turned away by the form.
 */
export const careersApply = z
  .object({
    name: text(120).min(1, "Type your name."),
    phone,
    email: z.email().max(200).optional(),
    zip,
    about: optional(1000),
    ...applyAnswers,
    /** The words next to the texts box, saved as proof when they said yes. */
    consentWords: optional(400),
    ...siteGuard,
  })
  .strict();
export type CareersApply = z.infer<typeof careersApply>;

/** A guest post pitch from the Write for Us page (signups.php steward_api_pitch). The writer's own site is `site`. */
export const sitePitch = z
  .object({
    name: optional(100),
    email: z.email("That email does not look right.").max(200),
    site: optional(200),
    topic: optional(200),
    summary: optional(3000),
    past: optional(1000),
    page: optional(300),
    ...siteGuard,
  })
  .strict();
export type SitePitch = z.infer<typeof sitePitch>;

export const siteAnswer = z.object({ ok: z.literal(true), message: z.string() });

// Office side ---------------------------------------------------------------------------------------------------------

export const territoriesShape = z.object({ zips: z.array(zip).max(2000) }).strict();

/** The board (owner 2026-11-04): new to hired, then Not now (the rehire pool), Not a fit and Archived under it. */
export const APPLICANT_STAGES = [
  "new",
  "talking",
  "screen",
  "trial",
  "offer",
  "paperwork",
  "hired",
  "not_now",
  "no_fit",
  "archived",
] as const;
export type ApplicantStage = (typeof APPLICANT_STAGES)[number];
export const APPLICANT_STAGE_WORDS: Record<ApplicantStage, string> = {
  new: "New",
  talking: "Talking",
  screen: "Phone screen",
  trial: "Trial day",
  offer: "Offer",
  paperwork: "Paperwork",
  hired: "Hired",
  not_now: "Not now",
  no_fit: "Not a fit",
  /** Off the board, in the Archived list under it; can be put back. */
  archived: "Archived",
};

export const applicantRow = z.object({
  id: z.uuid(),
  name: z.string(),
  phone: z.string().nullable(),
  email: z.string().nullable(),
  zip: z.string().nullable(),
  position: z.string().nullable(),
  about: z.string().nullable(),
  stage: z.enum(APPLICANT_STAGES),
  source: z.string(),
  channel: z.string().nullable(),
  personId: z.uuid().nullable(),
  createdAt: z.string(),
  language: z.enum(APPLY_LANGS),
  role: z.string().nullable(),
  days: z.array(z.number()).nullable(),
  ride: z.boolean().nullable(),
  workOk: z.boolean().nullable(),
  start: z.string().nullable(),
  textOk: z.boolean(),
  src: z.string().nullable(),
  /** The crew member who sent them. */
  referredBy: z.string().nullable(),
  reason: z.enum(NO_FIT_REASONS).nullable(),
  scores: screenScores,
  /** Their phone screen, booked from our text. */
  screenAt: z.string().nullable(),
  screenConfirmed: z.boolean(),
  /** When we first texted them, or null. */
  firstTextAt: z.string().nullable(),
  /** The page they pick a phone screen time on. */
  bookingLink: z.string().nullable(),
  stageChangedAt: z.string(),
  // Phase 2: the paid trial day, the offer and the paperwork.
  trialAt: z.string().nullable(),
  /** Who they ride with on the trial day. */
  trialLead: z.string().nullable(),
  trialAddress: z.string().nullable(),
  trialConfirmed: z.boolean(),
  trialScores: trialScores,
  trialNote: z.string().nullable(),
  trialHours: z.number().nullable(),
  /** Trial pay, hours times the role rate kept when it was booked. */
  trialPayCents: z.number().nullable(),
  trialPaid: z.boolean(),
  offer: z
    .object({
      letter: z.string(),
      startOn: z.string(),
      payType: z.enum(["percent", "hourly"]),
      percent: z.number().nullable(),
      hourlyCents: z.number().nullable(),
      sentAt: z.string(),
    })
    .nullable(),
  offerSignedAt: z.string().nullable(),
  offerSignedName: z.string().nullable(),
  paperwork: z.object({
    w9: z
      .object({
        legalName: z.string(),
        business: z.string(),
        tinType: z.enum(["ssn", "ein"]),
        tinLast4: z.string(),
        address: z.string(),
        signedAt: z.string(),
      })
      .nullable(),
    contact: z.object({ name: z.string(), phone: z.string(), relation: z.string(), shirt: z.string() }).nullable(),
    idFile: z.boolean(),
    handbookAt: z.string().nullable(),
    lessonsDone: z.number(),
    lessonsTotal: z.number(),
    doneAt: z.string().nullable(),
  }),
});
export type ApplicantRow = z.infer<typeof applicantRow>;

export const applicantInput = z
  .object({
    name: text(120).min(1, "Type a name."),
    phone: phone.optional(),
    email: z.email().max(200).optional(),
    zip: zip.optional(),
    position: optional(80),
    about: optional(3000),
    language: z.enum(APPLY_LANGS).optional(),
    role: optional(30),
    /** They said yes to texts (on the phone or in person). */
    textOk: z.boolean().optional(),
  })
  .strict();
export const applicantPatch = z
  .object({
    stage: z.enum(APPLICANT_STAGES).optional(),
    reason: z.enum(NO_FIT_REASONS).nullable().optional(),
    scores: screenScores.optional(),
    role: optional(30),
    language: z.enum(APPLY_LANGS).optional(),
    /** Send the kind close-out text when moving to Not now or Not a fit (default yes when texts are on). */
    tell: z.boolean().optional(),
    /** Hours worked on the trial day (pay is hours times the trial rate). */
    trialHours: z.number().min(0).max(24).nullable().optional(),
    /** The office paid the trial day. */
    trialPaid: z.boolean().optional(),
  })
  .strict();
export type ApplicantPatch = z.infer<typeof applicantPatch>;
export type ApplicantInput = z.infer<typeof applicantInput>;

export const signupRow = z.object({
  id: z.uuid(),
  kind: z.string(),
  name: z.string().nullable(),
  email: z.string().nullable(),
  phone: z.string().nullable(),
  zip: z.string().nullable(),
  offer: z.string().nullable(),
  channel: z.string().nullable(),
  createdAt: z.string(),
});
export type SignupRow = z.infer<typeof signupRow>;

/** A short name for where a visit came from, from its touch. Paid clicks first, then the source, then the referrer. */
export function channelOf(t: Touch | undefined): string {
  if (!t) return "direct";
  if (t.gclid) return "google_ads";
  if (t.fbclid) return t.utm_medium && /cpc|paid|ad/i.test(t.utm_medium) ? "facebook_ads" : "facebook";
  if (t.msclkid) return "bing_ads";
  const src = (t.utm_source ?? "").toLowerCase();
  const med = (t.utm_medium ?? "").toLowerCase();
  if (src) {
    if (/gbp|google_business|gmb/.test(src) || /gbp|maps/.test(med)) return "google_business";
    if (src.includes("google")) return /cpc|paid|ppc/.test(med) ? "google_ads" : "google";
    if (/facebook|fb|instagram|ig|meta/.test(src)) return /cpc|paid|ad/.test(med) ? "facebook_ads" : "facebook";
    if (/email|newsletter/.test(src) || med === "email") return "email";
    return src.replace(/[^a-z0-9]+/g, "_").slice(0, 30);
  }
  const ref = (t.referrer ?? "").toLowerCase();
  if (ref.includes("google.")) return "google";
  if (ref.includes("bing.")) return "bing";
  if (/facebook\.|instagram\./.test(ref)) return "facebook";
  if (ref.includes("yelp.")) return "yelp";
  if (ref.includes("nextdoor.")) return "nextdoor";
  return ref ? "referral" : "direct";
}
